import { useState, useEffect } from "react";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import useWikiCases from "./useWikiCases";
import { calcMargin } from "./utils";
import { GRID_SECTIONS } from "./constants";
import Banner from "./Banner";
import Overview from "./Overview";
import SectionModal from "./SectionModal";

import { useCompanyContext } from "../../../context";

const isDD = process.env.REACT_APP_IS_DD === "true";

const MainWiki = () => {
  const {
    caseNames,
    currentCompany: wikiCompany,
    setCurrentCompany,
  } = useWikiCases();

  const { currentCompany: ddCompany, companyKey } = useCompanyContext();

  const currentCompany = isDD ? (ddCompany?.case ?? null) : wikiCompany;

  const [baseMargin, setBaseMargin] = useState(() =>
    calcMargin(window.innerWidth),
  );
  const [activeModal, setActiveModal] = useState(null); // null | section string

  const openModal = (section) => {
    if (GRID_SECTIONS.includes(section)) setActiveModal(section);
  };
  const closeModal = () => setActiveModal(null);

  // Company selection from sidebar
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.company !== undefined) setCurrentCompany(e.detail.company);
    };
    window.addEventListener("sidebar:wiki-company-selected", handler);
    return () =>
      window.removeEventListener("sidebar:wiki-company-selected", handler);
  }, [setCurrentCompany]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("sidebar:select-company", {
        detail: { company: currentCompany },
      }),
    );
  }, [currentCompany]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("sidebar:refresh-cases"));
  }, [caseNames]);

  // Sidebar subsection clicks now open the modal instead of navigating
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.subSection) openModal(e.detail.subSection);
    };
    window.addEventListener("sidebar:wiki-subsection-selected", handler);
    return () =>
      window.removeEventListener("sidebar:wiki-subsection-selected", handler);
  }, []);

  // Close modal whenever the active company changes
  useEffect(() => {
    closeModal();
  }, [companyKey, currentCompany]);

  useEffect(() => {
    const handleResize = () => {
      const m = calcMargin(window.innerWidth);
      setBaseMargin((prev) => (prev !== m ? m : prev));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!currentCompany) {
    return (
      <div className="flex h-full items-center justify-center font-poppins font-normal">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col font-poppins font-normal text-mainText">
      {/* Banner is always visible now — no more hiding it on subsections */}
      <Banner currentCompany={currentCompany} baseMargin={baseMargin} />

      <div className="flex-1 overflow-y-auto">
        <Overview
          currentCase={currentCompany}
          setCurrentSubSection={openModal}
          currentCompany={currentCompany}
          baseMargin={baseMargin}
        />
      </div>

      <SectionModal
        section={activeModal}
        currentCompany={currentCompany}
        baseMargin={baseMargin}
        onClose={closeModal}
      />
    </div>
  );
};

export default MainWiki;
